/**
 * One-off publish channels (owner request 2026-10-09: "Some channels connectors should not work
 * like 'Calendar' and rather should work as binary 'is it published there'").
 *
 * A one-off channel takes ONE asset ONE time: a full-song video goes to YouTube, a Canvas loop goes
 * to Spotify. It is not a post of the Calendar. Per asset and channel the API keeps one publish
 * record (`AssetPublishModel`); the cards show a binary badge ("On YouTube" / "Not on YouTube").
 *
 * - YouTube (the full-song videos: lyric video, visualizer, music video). The API uploads the file
 *   as private and YouTube makes it public at `publishAt` (`status.publishAt`). Shorts stay in the
 *   Calendar.
 * - Spotify Canvas. Spotify has no public Canvas API. With a Spotify for Artists sign-in, Music24
 *   uploads the loop through the S4A web calls (`spotifyCanvas.ts`; the record has `viaApi` and the
 *   answer `mode: 'api'`). Without it, the artist sets the loop in Spotify for Artists by hand and
 *   marks it here ("I set it in Spotify for Artists"). The channel info stays `manual`: the native
 *   copies show the mark.
 * - Facebook (a full-song video on the Page, `/{page}/videos`, with `scheduled_publish_time`).
 * - Instagram (the full-song videos): "Post now as a Reel" through the API, one click, no schedule
 *   (the Instagram API cannot schedule). A video over the Reel limits (`postVideoRules.ts`) keeps
 *   the manual mark ("I posted it on Instagram") as the fallback (owner decision 2026-10-09).
 *
 * The web app, the API and the tests read this file. iOS and Android carry a copy of the rules
 * (`OneOffPublishRules.swift`, `OneOffPublishRules.kt`); change the three together. No imports.
 */

export const ONE_OFF_CHANNELS = [
  'youtube',
  'spotifyCanvas',
  'facebook',
  'instagram',
  'appleMusic',
  'youtubeBanner',
  'xBanner',
  'facebookBanner',
  'spotifyHeader',
  'youtubeThumbnail',
  'soundcloud',
] as const;
export type OneOffChannel = (typeof ONE_OFF_CHANNELS)[number];

/**
 * The manual marks of the Kit tab (design `assets-rethink` b1, b3; owner decisions 5 and 6,
 * 2026-10-09): Apple Music motion artwork ("I added it"), the three profile banners and the YouTube
 * thumbnail ("I set it"). They are records of the same table as every one-off channel.
 *
 * The iOS and Android builds of 2026-10-09 know only the first four channels, and iOS refuses a
 * list with an unknown channel. So `GET asset-publishes` leaves these out unless the client asks
 * for them (`?channels=all`, the web app). A native follow-up adds them to the native copies.
 */
export const KIT_MARK_CHANNELS = [
  'appleMusic',
  'youtubeBanner',
  'xBanner',
  'facebookBanner',
  'spotifyHeader',
  'youtubeThumbnail',
] as const satisfies readonly OneOffChannel[];

/**
 * The channels that take the SONG (its master, cover and facts), not a kit file (owner decision
 * 2026-10-15, spec `2026-10-15-music24-soundcloud-connector-design.md`). A record of these channels
 * has `catalogItemId` and no `kitAssetId`. The native apps do not know them yet, so they are out of
 * the list default too (see `KIT_MARK_CHANNELS`).
 */
export const SONG_PUBLISH_CHANNELS = ['soundcloud'] as const satisfies readonly OneOffChannel[];
export type SongPublishChannel = (typeof SONG_PUBLISH_CHANNELS)[number];

/** The channels that every client knows: the list default (see `KIT_MARK_CHANNELS`). */
export const NATIVE_ONE_OFF_CHANNELS: readonly OneOffChannel[] = ONE_OFF_CHANNELS.filter(
  (channel) =>
    !(KIT_MARK_CHANNELS as readonly string[]).includes(channel) &&
    !(SONG_PUBLISH_CHANNELS as readonly string[]).includes(channel)
);

export function isOneOffChannel(value: unknown): value is OneOffChannel {
  return typeof value === 'string' && (ONE_OFF_CHANNELS as readonly string[]).includes(value);
}

/** `api`: Music24 uploads the file. `manual`: the artist publishes it and marks it here. */
export type OneOffPublishMode = 'api' | 'manual';

/** The asset types (`StudioThemeId`) of the full-song videos. */
export const FULL_SONG_VIDEO_THEME_IDS = ['lyricVideo', 'visualizer', 'musicVideo'] as const;

export interface OneOffChannelInfo {
  channel: OneOffChannel;
  mode: OneOffPublishMode;
  /** What goes out: a kit file (`kitAssetId`) or the song itself (`catalogItemId`). */
  target: 'kitAsset' | 'song';
  /** The asset types that this channel takes. */
  themeIds: readonly string[];
  /** The outputs (`variant`) that this channel takes; absent: every output of the types. */
  variants?: readonly string[];
  /** The Promote channel whose login uploads the file. Null for a manual channel. */
  socialPlatform: 'youtube' | 'facebook' | 'instagram' | null;
  /** The upload can wait for a time ("On release day", "Later"). */
  canSchedule: boolean;
  /** A waiting time can be changed or cancelled before the video is public. */
  canChangeTime: boolean;
  /** The Calendar platform that this channel replaces for its asset types (the Composer hides it). */
  replacesCalendarPlatform: 'youtube' | 'facebook' | 'instagram' | null;
  /** An API channel that also takes a manual mark when the platform refuses the file (Instagram). */
  manualFallback: boolean;
  /** A newer revision can replace the video that is live (YouTube): the old one turns private. */
  canReplace: boolean;
}

export const ONE_OFF_CHANNEL_INFO: Readonly<Record<OneOffChannel, OneOffChannelInfo>> = {
  youtube: {
    channel: 'youtube',
    target: 'kitAsset',
    mode: 'api',
    themeIds: FULL_SONG_VIDEO_THEME_IDS,
    socialPlatform: 'youtube',
    canSchedule: true,
    canChangeTime: true,
    replacesCalendarPlatform: 'youtube',
    manualFallback: false,
    canReplace: true,
  },
  spotifyCanvas: {
    channel: 'spotifyCanvas',
    target: 'kitAsset',
    mode: 'manual',
    themeIds: ['canvasLoop'],
    socialPlatform: null,
    canSchedule: false,
    canChangeTime: false,
    replacesCalendarPlatform: null,
    manualFallback: false,
    canReplace: false,
  },
  facebook: {
    channel: 'facebook',
    target: 'kitAsset',
    mode: 'api',
    themeIds: FULL_SONG_VIDEO_THEME_IDS,
    socialPlatform: 'facebook',
    canSchedule: true,
    // The Graph API takes `scheduled_publish_time` at the upload. A change after it is not
    // confirmed against a live Page, so the cards do not offer it.
    canChangeTime: false,
    replacesCalendarPlatform: 'facebook',
    manualFallback: false,
    canReplace: false,
  },
  instagram: {
    channel: 'instagram',
    target: 'kitAsset',
    mode: 'api',
    themeIds: FULL_SONG_VIDEO_THEME_IDS,
    socialPlatform: 'instagram',
    // The Instagram API publishes a Reel at once; it has no scheduled publish.
    canSchedule: false,
    canChangeTime: false,
    replacesCalendarPlatform: 'instagram',
    manualFallback: true,
    canReplace: false,
  },
  // Apple Music takes the motion artwork from the distributor: the artist uploads it and marks it.
  appleMusic: manualMark('appleMusic', ['motionArtwork']),
  // The profile banners: each site takes its own banner file (the variant of the banners type).
  youtubeBanner: manualMark('youtubeBanner', ['banners'], ['youtube']),
  xBanner: manualMark('xBanner', ['banners'], ['x']),
  facebookBanner: manualMark('facebookBanner', ['banners'], ['facebook']),
  // Spotify has no API for the artist header: the artist uploads it in Spotify for Artists.
  spotifyHeader: manualMark('spotifyHeader', ['spotifyHeader']),
  // The YouTube upload does not set the thumbnail yet: the artist sets it in YouTube Studio.
  youtubeThumbnail: manualMark('youtubeThumbnail', ['thumbnail']),
  // The song's master goes up through the official SoundCloud API with the login that MusicAPI
  // holds. SoundCloud has no scheduled public release on a free account, so Music24 waits and
  // uploads at the time ("On release day", "Later").
  soundcloud: {
    channel: 'soundcloud',
    target: 'song',
    mode: 'api',
    themeIds: [],
    socialPlatform: null,
    canSchedule: true,
    canChangeTime: true,
    replacesCalendarPlatform: null,
    manualFallback: false,
    canReplace: false,
  },
};

function manualMark(
  channel: OneOffChannel,
  themeIds: readonly string[],
  variants?: readonly string[]
): OneOffChannelInfo {
  return {
    channel,
    target: 'kitAsset',
    mode: 'manual',
    themeIds,
    ...(variants === undefined ? {} : { variants }),
    socialPlatform: null,
    canSchedule: false,
    canChangeTime: false,
    manualFallback: false,
    canReplace: false,
    replacesCalendarPlatform: null,
  };
}

/**
 * The one-off channels of an asset type, in the badge order. Empty for every other asset type. With
 * a `variant`, a channel that takes only some outputs (a banner of one site) must take that one.
 */
export function oneOffChannelsOfTheme(themeId: string, variant?: string): OneOffChannel[] {
  return ONE_OFF_CHANNELS.filter((channel) => {
    const info = ONE_OFF_CHANNEL_INFO[channel];
    if (!info.themeIds.includes(themeId)) {
      return false;
    }
    return variant === undefined || info.variants === undefined || info.variants.includes(variant);
  });
}

export function isFullSongVideoTheme(themeId: string | null | undefined): boolean {
  return typeof themeId === 'string' && (FULL_SONG_VIDEO_THEME_IDS as readonly string[]).includes(themeId);
}

/**
 * The Calendar platforms that the Composer must not offer for an asset of this type: a one-off
 * channel takes it instead. A Short (and every other asset type) keeps every platform.
 */
export function calendarPlatformsReplacedForTheme(themeId: string | null | undefined): string[] {
  if (typeof themeId !== 'string') {
    return [];
  }
  return oneOffChannelsOfTheme(themeId)
    .map((channel) => ONE_OFF_CHANNEL_INFO[channel].replacesCalendarPlatform)
    .filter((platform): platform is 'youtube' | 'facebook' | 'instagram' => platform !== null);
}

/**
 * The state of one publish record.
 * - `waiting`: the user switched "Publish when ready" on; the asset still renders.
 * - `uploading`: the job sends the file.
 * - `scheduled`: the platform holds the video private until `publishAt`.
 * - `published`: the video is public (or the artist marked a manual channel).
 * - `failed`: the upload failed; `failReason` says why.
 * - `removed`: the video is gone, or private on the platform (`removedReason`).
 * - `canceled`: the user cancelled a scheduled publish; the video stays private on the platform.
 */
export const ASSET_PUBLISH_STATUSES = [
  'waiting',
  'uploading',
  'scheduled',
  'published',
  'failed',
  'removed',
  'canceled',
] as const;
export type AssetPublishStatus = (typeof ASSET_PUBLISH_STATUSES)[number];

/**
 * - `deleted`: the video is gone on the platform.
 * - `private`: the video is private on the platform (and has no time to go public).
 * - `replaced`: a newer revision replaced it; Music24 made the old video private (not deleted).
 */
export type AssetPublishRemovedReason = 'deleted' | 'private' | 'replaced';

/**
 * When the video goes public (owner decision 2026-10-09: "allow user to pick if this should be
 * published now or later").
 * - `now`: public at once.
 * - `releaseDay`: private now, public at the release day of the song (the default while the
 *   release day is in the future).
 * - `later`: private now, public at a time that the user picks.
 */
export const PUBLISH_TIMINGS = ['now', 'releaseDay', 'later'] as const;
export type PublishTiming = (typeof PUBLISH_TIMINGS)[number];

export function isPublishTiming(value: unknown): value is PublishTiming {
  return typeof value === 'string' && (PUBLISH_TIMINGS as readonly string[]).includes(value);
}

/**
 * A scheduled time must be this far in the future. YouTube refuses a `publishAt` in the past, and
 * Facebook asks for at least 10 minutes. A nearer time publishes at once.
 */
export const MIN_PUBLISH_LEAD_MS = 15 * 60 * 1000;
/** Facebook takes a scheduled time at most 6 months ahead (YouTube has no limit). */
export const FACEBOOK_MAX_SCHEDULE_MS = 180 * 24 * 60 * 60 * 1000;

/**
 * The start of the release day: 00:00 of `YYYY-MM-DD` in the time zone of the user.
 * `timezoneOffsetMinutes` has the sign of `Date.prototype.getTimezoneOffset` (UTC minus local, so
 * Berlin in summer is -120). 0: 00:00 UTC. Null for a value that is not a day.
 */
export function releaseDayStart(releaseDate: string | null | undefined, timezoneOffsetMinutes = 0): Date | null {
  if (typeof releaseDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(releaseDate)) {
    return null;
  }
  const midnightUtc = Date.parse(`${releaseDate}T00:00:00.000Z`);
  if (Number.isNaN(midnightUtc)) {
    return null;
  }
  return new Date(midnightUtc + timezoneOffsetMinutes * 60 * 1000);
}

/** True when a time is far enough in the future to wait for it. */
export function isSchedulableTime(at: Date | null, now: Date): boolean {
  return at !== null && at.getTime() - now.getTime() >= MIN_PUBLISH_LEAD_MS;
}

/** The choices that the timing picker shows: "On release day" only while that day is ahead. */
export function publishTimingChoices(channel: OneOffChannel, releaseAt: Date | null, now: Date): PublishTiming[] {
  if (!ONE_OFF_CHANNEL_INFO[channel].canSchedule) {
    return ['now'];
  }
  return isSchedulableTime(releaseAt, now) ? ['now', 'releaseDay', 'later'] : ['now', 'later'];
}

/** The pre-selected timing: "On release day" while it is ahead, else "Now". */
export function defaultPublishTiming(channel: OneOffChannel, releaseAt: Date | null, now: Date): PublishTiming {
  return publishTimingChoices(channel, releaseAt, now).includes('releaseDay') ? 'releaseDay' : 'now';
}

/**
 * The time that the platform makes the video public, or null for "public at once". A release day
 * or a picked time that is not far enough ahead (`MIN_PUBLISH_LEAD_MS`) publishes at once.
 */
export function resolvePublishAt(params: {
  timing: PublishTiming;
  releaseAt: Date | null;
  pickedAt: Date | null;
  now: Date;
}): Date | null {
  const { timing, releaseAt, pickedAt, now } = params;
  const at = timing === 'releaseDay' ? releaseAt : timing === 'later' ? pickedAt : null;
  return isSchedulableTime(at, now) ? at : null;
}

/** The record as the API answers it. */
export interface SerializedAssetPublish {
  uuid: string;
  kitAssetUuid: string;
  /** The asset type and output of the kit asset: a card shows the newest record of its type. */
  themeId: string;
  variant: string;
  /** The revision (r1, r2) of the kit asset that went out. */
  revision: number;
  catalogItemUuid: string;
  channel: OneOffChannel;
  mode: OneOffPublishMode;
  status: AssetPublishStatus;
  timing: PublishTiming;
  /** ISO time. The time the video goes public (scheduled), or went public (published by schedule). */
  publishAt: string | null;
  /** ISO time the video was public for the first time, as far as Music24 saw it. */
  publishedAt: string | null;
  /** The id of the video on the platform (YouTube video id, Facebook video id). */
  externalId: string | null;
  /** The watch page of the video. */
  externalUrl: string | null;
  title: string | null;
  madeForKids: boolean | null;
  failReason: string | null;
  removedReason: AssetPublishRemovedReason | null;
  /** False when the stored login lacks the scope that changes the time (Reconnect to change it). */
  canChangeTime: boolean;
  /** The record that this one replaces (a newer revision on YouTube); null for none. */
  replacesPublishUuid: string | null;
  createdAt: string;
  updatedAt: string | null;
}

/** `GET /accounts/:accountId/asset-publishes[?catalogItemUuid=]` */
export interface AssetPublishListResponse {
  publishes: SerializedAssetPublish[];
}

/** The Promote channel that would upload the file. */
export interface AssetPublishConnection {
  connected: boolean;
  /** The channel name and avatar (the YouTube terms ask the posting page to show them). */
  displayName: string | null;
  avatarUrl: string | null;
  needsReconnect: boolean;
}

/** `GET /accounts/:accountId/asset-publishes/defaults?kitAssetUuid=&channel=` */
export interface AssetPublishDefaultsResponse {
  kitAssetUuid: string;
  channel: OneOffChannel;
  mode: OneOffPublishMode;
  /** Null for a manual channel. */
  connection: AssetPublishConnection | null;
  /**
   * Always true since 2026-10-10. Kept so that an app from before that day shows no "private until
   * Google verifies Music24" note: that note was wrong. YouTube publishes public videos of
   * Music24; the poll reads the real state of each video.
   */
  auditedApp: boolean;
  title: string;
  description: string;
  tags: string[];
  /** ISO 'YYYY-MM-DD' release day of the song, or null. */
  releaseDate: string | null;
  /** The newest record of this asset version on this channel, or null. */
  current: SerializedAssetPublish | null;
  /**
   * A live record of ANOTHER revision of the same asset type on this channel (YouTube): the form
   * asks "Replace the video on YouTube?" (replace, the default, or keep both). Null for none.
   */
  otherLive: SerializedAssetPublish | null;
  /**
   * Why the platform refuses this file (Instagram Reel limits: size, length, shape), or null. With a
   * refusal the dialog says why and offers the manual mark (`manualFallback`).
   */
  refusal: AssetPublishRefusal | null;
}

/** The Reel limit that a file breaks (fym-shared `postVideoRules.ts`). */
export type AssetPublishRefusal =
  | { reason: 'size'; sizeBytes: number; maxBytes: number }
  | { reason: 'tooShort'; durationSeconds: number; minSeconds: number }
  | { reason: 'tooLong'; durationSeconds: number; maxSeconds: number }
  | { reason: 'aspect'; ratio: number; minRatio: number; maxRatio: number }
  | { reason: 'type'; mimeType: string; allowed: ReadonlyArray<string> };

/** `POST /accounts/:accountId/asset-publishes` */
export interface CreateAssetPublishBody {
  kitAssetUuid: string;
  channel: OneOffChannel;
  timing: PublishTiming;
  /** ISO time. Required for `later`; for `releaseDay` the start of the release day in the user's zone. */
  publishAt?: string | null;
  title?: string;
  description?: string;
  /** Required for YouTube (the YouTube terms: no default). */
  madeForKids?: boolean;
  /** A manual mark on an API channel with `manualFallback` (Instagram: the file breaks a Reel limit). */
  manual?: boolean;
  /** Replace this live record of another revision (YouTube): once the new video is public, the old one turns private. */
  replacesPublishUuid?: string | null;
  /** Keep the live video of the other revision and upload a second one. */
  keepBoth?: boolean;
}

/** `PATCH /accounts/:accountId/asset-publishes/:publishUuid` */
export interface UpdateAssetPublishBody {
  timing: PublishTiming;
  publishAt?: string | null;
}

/** `{ publish }` of POST, PATCH and DELETE. */
export interface AssetPublishResponse {
  publish: SerializedAssetPublish | null;
}

export const YOUTUBE_TITLE_MAX_LENGTH = 100;
export const YOUTUBE_DESCRIPTION_MAX_LENGTH = 5000;

/** The badge of one channel on an asset card. */
export type PublishBadgeState =
  | { kind: 'none' }
  | { kind: 'waiting' }
  | { kind: 'uploading' }
  | { kind: 'scheduled'; publishAt: string | null }
  | { kind: 'published'; url: string | null }
  | { kind: 'failed'; reason: string | null }
  | { kind: 'removed'; reason: AssetPublishRemovedReason | null }
  | { kind: 'canceled' };

export function publishBadgeState(record: SerializedAssetPublish | null | undefined): PublishBadgeState {
  if (record === null || record === undefined) {
    return { kind: 'none' };
  }
  switch (record.status) {
    case 'waiting':
      return { kind: 'waiting' };
    case 'uploading':
      return { kind: 'uploading' };
    case 'scheduled':
      return { kind: 'scheduled', publishAt: record.publishAt };
    case 'published':
      return { kind: 'published', url: record.externalUrl };
    case 'failed':
      return { kind: 'failed', reason: record.failReason };
    case 'removed':
      return { kind: 'removed', reason: record.removedReason };
    case 'canceled':
      return { kind: 'canceled' };
  }
}

/**
 * The record that the card of an asset type shows for one channel: a live record of any revision
 * of that type first (a newer revision does not hide the video that is on YouTube), else the newest
 * record. `publishes` comes newest first from the API.
 */
export function publishOfAssetType(
  publishes: readonly SerializedAssetPublish[],
  asset: { catalogItemUuid: string; themeId: string; variant: string },
  channel: OneOffChannel
): SerializedAssetPublish | null {
  const ofType = publishes.filter(
    (one) =>
      one.channel === channel &&
      one.catalogItemUuid === asset.catalogItemUuid &&
      one.themeId === asset.themeId &&
      one.variant === asset.variant
  );
  const live = ofType.find((one) => !canStartPublish(one));
  return live ?? ofType[0] ?? null;
}

/** True when the badge is the binary "on it" state. */
export function isOnChannel(record: SerializedAssetPublish | null | undefined): boolean {
  return record?.status === 'published';
}

/** True when a new publish may start (no live record). */
export function canStartPublish(record: SerializedAssetPublish | null | undefined): boolean {
  return (
    record === null ||
    record === undefined ||
    record.status === 'failed' ||
    record.status === 'removed' ||
    record.status === 'canceled'
  );
}

/** True when the waiting time can still change (before the video is public). */
export function canChangeScheduledTime(record: SerializedAssetPublish | null | undefined, now: Date): boolean {
  if (record === null || record === undefined) {
    return false;
  }
  if (!ONE_OFF_CHANNEL_INFO[record.channel].canChangeTime || !record.canChangeTime) {
    return false;
  }
  if (record.status === 'waiting') {
    return true;
  }
  return record.status === 'scheduled' && record.publishAt !== null && Date.parse(record.publishAt) > now.getTime();
}

/** A YouTube video id: 11 characters of the URL-safe base64 alphabet. */
export function isYouTubeVideoId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{11}$/.test(value);
}

/** The watch page of a YouTube video. */
export function youtubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

/**
 * The embed address of a YouTube video. The no-cookie host sets no cookie before the fan plays the
 * video (the consent rules of the SmartLink pages); YouTube counts the views of both hosts.
 */
export function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`;
}

/**
 * One channel of a Kit slot with all of its files (owner fix 2026-10-10: a slot showed one badge
 * per file, so the four YouTube thumbnails said "Not set on YouTube" four times). A slot shows ONE
 * badge per channel. `marked` counts the files that are on the channel (`isOnChannel`).
 */
export interface SlotChannelGroup<E extends { channel: OneOffChannel; record: SerializedAssetPublish | null }> {
  channel: OneOffChannel;
  /** The files of the slot that this channel takes, in slot order. */
  entries: E[];
  total: number;
  marked: number;
}

/** Groups the per-file channel entries of a slot by channel, in the order of their first entry. */
export function groupSlotChannels<E extends { channel: OneOffChannel; record: SerializedAssetPublish | null }>(
  entries: readonly E[]
): Array<SlotChannelGroup<E>> {
  const groups: Array<SlotChannelGroup<E>> = [];
  for (const entry of entries) {
    let group = groups.find((one) => one.channel === entry.channel);
    if (group === undefined) {
      group = { channel: entry.channel, entries: [], total: 0, marked: 0 };
      groups.push(group);
    }
    group.entries.push(entry);
    group.total += 1;
    group.marked += isOnChannel(entry.record) ? 1 : 0;
  }
  return groups;
}
